import { IconNames } from "../common/icon/LucideIcon";
import { CustomType, customTypeConstants, CustomTypeFilters } from "./CustomTypes/CustomTypes";
import {
  PropertyTranslator,
  IGenericRequestParametersTranslator,
  IPaginationParametersTranslator,
  ISoftDeletableFilterParametersTranslator,
  IEntryRecordParametersTranslator,
  IModifiedRecordParametersTranslator,
  IHierarchyParametersTranslator,
  IProjectBasedParametersTranslator,
  IEntryRecordLabels,
  ICustomTypedEntityLabels,
  IModificationRecordLabels,
  INamedEntityLabels,
  IPermissionedEntityLabels,
  IRelatedEntityLabels,
  ISoftDeletableLabels,
  IUniqueEntityLabels,
  IHierarchyReadLabels,
  ILockableEntityLabels,
  ILockableEntityParametersTranslator,
  ISignableEntityLabels,
  ITypedEntityParametersTranslator,
  ISignableEntityParametersTranslator,
} from "./GenericTranslator";
import {
  EntryRecordOrderBy,
  ModificationRecordOrderBy,
  GenericEntityOrderBy,
  EntityConstants,
  ICreatedRecordParameters,
  IEntryRecord,
  IGenericRequestParameters,
  IModelWithIntId,
  IModificationRecord,
  IModifiedRecordParameters,
  INamedEntity,
  IPaginationParameters,
  IPermissionedEntity,
  IRelatedEntity,
  IRelationModel,
  ISoftDeletable,
  ISoftDeletableFilterParameters,
  ICustomTypedEntity,
  IUniqueEntity,
  TypedEntitySuggestionsModel,
  IRelation,
  IProjectBasedResponseModel,
  IHierarchyRead,
  IHierarchyParameters,
  DefaultHierarchyOrderTypeConsts,
  IEntityMinimalModel,
  IProjectBasedParameters,
  ILockableEntity,
  ITypedEntityParameters,
  ILockableEntityParameters,
  ISignableEntity,
  ISignableEntityParameters,
  TypedEntityOrderBy,
  NamedEntityOrderBy,
} from "./GenericTypes";

export const inventoriesConstants: EntityConstants<CustomType, CustomTypeFilters> = {
  resource: customTypeConstants.resource,
  frontendIndexRoute: "inventories",
  entitySingular: "inventory",
  entityPlural: "inventories",
  icon: "box",
};

export const inventoryItemsConstants: EntityConstants<InventoryItem, InventoryItemFilters> = {
  resource: "inventory_items",
  frontendIndexRoute: "inventory_items",
  entitySingular: "inventory item",
  entityPlural: "inventory items",
  icon: "file-box",
};

export const hierarchyConstants: {
  flatLayoutIcon: IconNames;
  hierarchyLayoutIcon: IconNames;
  childIcon: IconNames;
  childItemIcon: IconNames;
  childType: string;
  childTypeDescription: string;
} = {
  flatLayoutIcon: "list",
  hierarchyLayoutIcon: "network",
  childIcon: "git-pull-request-create-arrow",
  childItemIcon: "plus",
  childType: `${inventoryItemsConstants.entitySingular} type`,
  childTypeDescription: `${inventoryItemsConstants.entitySingular} type description`,
};

export interface InventoryItem
  extends IModelWithIntId,
    IPermissionedEntity,
    IRelatedEntity<InventoryItemRelations>,
    INamedEntity,
    IUniqueEntity,
    ISoftDeletable,
    IEntryRecord,
    IModificationRecord,
    ICustomTypedEntity,
    IProjectBasedResponseModel,
    IHierarchyRead,
    ILockableEntity,
    ISignableEntity {
  rootCustomType?: InventoryItemRootCustomTypeModel;
  isRootItem: boolean;
  isHierarchyItem: boolean;
  isDeletedViaHierarchy?: boolean;
  isLockedViaHierarchy?: boolean;
}

interface InventoryItemRootCustomTypeModel extends IEntityMinimalModel {
  inventoryName: string;
}

export const InventoryItemFieldLabels: PropertyTranslator<InventoryItem> = {
  ...INamedEntityLabels,
  ...IUniqueEntityLabels,
  ...IPermissionedEntityLabels,
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...IRelatedEntityLabels,
  ...ISoftDeletableLabels,
  ...ICustomTypedEntityLabels,
  ...IHierarchyReadLabels,
  ...ILockableEntityLabels,
  ...ISignableEntityLabels,
  id: "Inventory item ID",
  projects: "Projects",
  rootCustomType: "Inventory",
  isRootItem: "Is top level item",
  isHierarchyItem: "Is part of hierarchy",
  isDeletedViaHierarchy: "Is deleted via hierarchy",
  isLockedViaHierarchy: "Is locked via hierarchy",
} as const;

export interface InventoryItemSuggestions extends TypedEntitySuggestionsModel<InventoryItem["id"]> {}

export interface InventoryItemRelations extends IRelationModel {
  labNotebookEntries: IRelation;
}

export interface InventoryItemFilters
  extends IGenericRequestParameters<InventoryItem, InventoryItemSortingOptions>,
    IPaginationParameters,
    ISoftDeletableFilterParameters,
    ICreatedRecordParameters,
    IModifiedRecordParameters,
    ITypedEntityParameters,
    IHierarchyParameters,
    IProjectBasedParameters,
    ILockableEntityParameters,
    ISignableEntityParameters {}

export const InventoryItemSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...TypedEntityOrderBy,
  ...EntryRecordOrderBy,
  ...ModificationRecordOrderBy,
  ...DefaultHierarchyOrderTypeConsts,
] as const;
export type InventoryItemSortingOptions = (typeof InventoryItemSortingOptionsConsts)[number];

export const InventoryItemFiltersTranslator: PropertyTranslator<InventoryItemFilters> = {
  ...IGenericRequestParametersTranslator,
  ...IPaginationParametersTranslator,
  ...ISoftDeletableFilterParametersTranslator,
  ...IEntryRecordParametersTranslator,
  ...IModifiedRecordParametersTranslator,
  ...ITypedEntityParametersTranslator,
  ...IHierarchyParametersTranslator,
  ...IProjectBasedParametersTranslator,
  ...ILockableEntityParametersTranslator,
  ...ISignableEntityParametersTranslator,
} as const;

export const InventoryItemSearchTermOptions = {
  searchTermIncludeNames: InventoryItemFiltersTranslator["searchTermIncludeNames"],
  searchTermIncludeIds: InventoryItemFiltersTranslator["searchTermIncludeIds"],
  searchTermIncludeUuids: InventoryItemFiltersTranslator["searchTermIncludeUuids"],
  searchTermIncludeCustomFieldValues: InventoryItemFiltersTranslator["searchTermIncludeCustomFieldValues"],
} as const;
